import { useCallback, useEffect, useRef } from 'react';
import { registerUndoSeparator } from '../../../utils/undoSeparation';
import { cloneTickOverrides } from '../../../utils/tickOverrides';
import { cloneDocVariables } from '../utils/docVariablesUtils';

/**
 * Undo/redo stack primitives. Typing (coalesce) groups rapid keystrokes
 * within 600ms into one step; structural commits always push their own step.
 * Ctrl+Z / Ctrl+Y restore whole-document snapshots via useUndoRedo.
 */
export default function useHistoryStack({
  blocksRef,
  docVariablesRef,
  tickOverridesRef,
}) {
  const historyRef = useRef({ undo: [], redo: [], lastRecord: 0 });

  const captureSnapshot = useCallback(
    () => ({
      blocks: blocksRef.current,
      docVariables: cloneDocVariables(docVariablesRef.current),
      tickOverrides: cloneTickOverrides(tickOverridesRef?.current),
    }),
    [blocksRef, docVariablesRef, tickOverridesRef]
  );

  const pushHistory = useCallback(
    (coalesce) => {
      const h = historyRef.current;
      const now = Date.now();
      // Structural (!coalesce) always gets its own undo entry. Typing still
      // groups within the 600ms window. separateNextUndoStep() zeroes
      // lastRecord so paste/cut always open a fresh step.
      if (!coalesce || now - h.lastRecord > 600 || h.undo.length === 0) {
        h.undo.push(captureSnapshot());
        if (h.undo.length > 200) h.undo.shift();
      }
      h.lastRecord = now;
      h.redo = [];
    },
    [captureSnapshot]
  );

  // Paste/cut call separateNextUndoStep() so their follow-up content commit
  // isn't swallowed by the typing coalesce window.
  useEffect(
    () =>
      registerUndoSeparator(() => {
        historyRef.current.lastRecord = 0;
      }),
    []
  );

  return { historyRef, captureSnapshot, pushHistory };
}
